import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Footprints, Flame, Zap } from "lucide-react";
import { SFX } from "@/lib/sounds";
import GatherMinigame from "@/components/GatherMinigame";
import {
  TRAVEL_VISIONS, VISION_MIN_STEPS, VISION_CHANCE,
  pickVision, markVisionUnlocked, findVisionById, persistVisionState,
} from "@/lib/travelVisions";

// Fallback vision shown as backdrop before the player has rolled any
// panorama (fresh characters, or before step 10). Keeps parity with
// Market/Shop where the backdrop is always visible.
const DEFAULT_VISION = TRAVEL_VISIONS.find(v => v.id === "road") || TRAVEL_VISIONS[0];

const COOLDOWN_MS = 3000;

export default function TravelPanel({ ch, setCh }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [cooldownEnds, setCooldownEnds] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [minigame, setMinigame] = useState(null);
  // Vision state — hydrated from the persisted `ch.travel_vision_state`
  // so the panorama survives refresh + logout/login. The client only
  // owns the RNG roll; the truth is on the character doc.
  const persisted = ch?.travel_vision_state || { current: null, history: [], step_counter: 0 };
  const [vision, setVision] = useState(() => findVisionById(persisted.current));
  // Backend-authoritative lifetime step counter — used to detect the
  // "every 10 walks" milestone crossing (see step() below). Avoids the
  // race condition of a local counter that gets rehydrated from a
  // fire-and-forget persist.
  const prevStepsRef = useRef(ch?.counters?.steps || 0);
  const visionHistoryRef = useRef(Array.isArray(persisted.history) ? persisted.history : []);

  // Re-hydrate whenever the character object changes (e.g. after login,
  // or after a claim/regen refetch that returns a fresh payload).
  useEffect(() => {
    const tvs = ch?.travel_vision_state || {};
    visionHistoryRef.current = Array.isArray(tvs.history) ? tvs.history : [];
    const restored = findVisionById(tvs.current);
    if (restored && (!vision || vision.id !== restored.id)) {
      setVision(restored);
      if (restored?.id) markVisionUnlocked(restored.id);
    }
    prevStepsRef.current = ch?.counters?.steps || 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ch?.user_id]);

  useEffect(() => {
    if (cooldownEnds > Date.now()) {
      const timer = setInterval(() => setNow(Date.now()), 200);
      return () => clearInterval(timer);
    }
  }, [cooldownEnds]);

  // On mount, resume any pending minigame (e.g. after page reload)
  useEffect(() => {
    let cancelled = false;
    api.minigameCurrent().then(res => {
      if (!cancelled && res?.active) setMinigame(res.minigame);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const cdRemaining = Math.max(0, cooldownEnds - now);
  const onCooldown = cdRemaining > 0;

  const step = async (fast = false) => {
    if (busy) return;
    if (!fast && onCooldown) return;
    setBusy(true);
    try {
      const prevLevel = ch.level;
      const res = fast ? await api.travelFast() : await api.travel();
      setCh(res.character);
      if (!fast) setCooldownEnds(Date.now() + COOLDOWN_MS);
      SFX.step();
      if (res.character.level > prevLevel) SFX.levelup();
      const o = res.outcome;
      const opts = { duration: 1800 };
      if (o.kind === "loot") {
        SFX.loot();
        toast(`+ ${o.item.name}`, opts);
      } else if (o.kind === "combat") {
        SFX.sword();
        toast(`⚔ ${o.enemy.name}`, opts);
      } else if (o.kind === "reward") {
        SFX.coin();
        toast(`+${o.gold}g · +${o.xp}xp`, opts);
      } else if (o.kind === "gather_offer") {
        SFX.loot();
        setMinigame({
          session_id: o.session_id,
          skill: o.skill,
          tier: o.tier,
          resource: o.resource,
          zone: o.zone,
          value: o.value,
          xp: o.xp,
          text: o.text,
        });
      }
      if (o.streak_bonus) {
        toast(`Cooldown Streak ×${o.streak} — +25% XP`, { duration: 2200 });
      }

      // Vision roll — deterministic: rotates every VISION_MIN_STEPS
      // lifetime walks (backend-authoritative counter). We detect the
      // milestone by comparing pre/post `counters.steps`, so combat and
      // gather outcomes still count toward the milestone even when the
      // per-step outcome flavor differs.
      const prevSteps = prevStepsRef.current;
      const newSteps = res.character?.counters?.steps || 0;
      prevStepsRef.current = newSteps;
      let rolledVision = null;
      let nextHistory = visionHistoryRef.current;
      const crossedMilestone =
        Math.floor(newSteps / VISION_MIN_STEPS) >
        Math.floor(prevSteps / VISION_MIN_STEPS);
      if (!minigame && crossedMilestone) {
        const picked = pickVision(visionHistoryRef.current, newSteps);
        nextHistory = [...visionHistoryRef.current, picked.id].slice(-5);
        visionHistoryRef.current = nextHistory;
        markVisionUnlocked(picked.id);
        rolledVision = picked;
        setVision(picked);
        SFX.visionStinger();
      }
      // Persist to backend — every step so the counter stays in sync
      // even if the walk is interrupted (fire-and-forget, no blocking).
      persistVisionState({
        current: (rolledVision ? rolledVision.id : (vision?.id || null)),
        history: nextHistory,
        step_counter: newSteps,
      });
    } catch (e) {
      SFX.denied();
      toast.error(e?.response?.data?.detail || "The road refuses you.", { duration: 1600 });
    } finally {
      setBusy(false);
    }
  };

  const log = useMemo(() => [...(ch.adventure_log || [])].reverse().slice(0, 40), [ch.adventure_log]);

  // Live translation of adventure_log entries into the player's language.
  // Non-Italian only. Batches every uncached text into a single backend call
  // that hits Gemini 3 Flash (server-side cache prevents repeat work).
  const { i18n } = useTranslation();
  const [logTr, setLogTr] = useState({});
  useEffect(() => {
    const lang = (i18n.language || "").toLowerCase();
    if (!lang || lang === "it") { setLogTr({}); return; }
    const uniq = Array.from(new Set(log.map(e => e.text).filter(Boolean)));
    const missing = uniq.filter(t => !(t in logTr));
    if (missing.length === 0) return;
    let cancelled = false;
    api.translateTexts(missing, lang).then(r => {
      if (!cancelled) setLogTr(prev => ({ ...prev, ...(r?.translations || {}) }));
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log, i18n.language]);
  const streak = ch.fast_travel_streak || 0;

  return (
    <div className="relative isolate space-y-4" data-testid="travel-panel">
      {/* Ravencross vision backdrop — always visible: falls back to the
          default panorama until the walker rolls their first vision. */}
      {(vision || DEFAULT_VISION) && (
        <div
          aria-hidden="true"
          data-testid={`travel-vision-bg-${(vision || DEFAULT_VISION).id}`}
          className="pointer-events-none fixed inset-0 -z-10 transition-opacity duration-700"
          style={{
            backgroundImage: `url(${(vision || DEFAULT_VISION).url})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
            opacity: 0.55,
            filter: "saturate(0.9) contrast(1.05)",
            imageRendering: "pixelated",
          }}
        />
      )}
      <button
        data-testid="travel-button"
        onClick={() => step(false)}
        disabled={busy || onCooldown || ch.explore_points <= 0 || !!ch.combat}
        className="travel-btn w-full py-8 font-heading text-2xl uppercase tracking-[0.3em]"
      >
        <div className="flex items-center justify-center gap-3">
          <Footprints strokeWidth={1.2} className="w-6 h-6" />
          <span>
            {busy
              ? t("travel.walking")
              : onCooldown
                ? t("travel.wait", { seconds: Math.ceil(cdRemaining / 1000) })
                : t("travel.takeAWalk")}
          </span>
        </div>
        <div className="mt-2 font-mono text-[10px] text-[#8c8c8c] tracking-widest">
          {t("travel.cost", { current: ch.explore_points, max: ch.max_explore_points })}
        </div>
      </button>

      {streak >= 2 && (
        <div
          className="flex items-center justify-between px-3 py-2 border border-[#B89C3A]/40 bg-[#0a0a0a]"
          data-testid="cooldown-streak"
        >
          <div className="flex items-center gap-2">
            <Flame className="w-3 h-3 text-[#E5C34A]" strokeWidth={1.5} />
            <span className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#B89C3A]">
              {t("travel.streak")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-[#E2DFD8]" data-testid="streak-count">×{streak}</span>
            <span className="font-mono text-[9px] text-[#666] tracking-widest uppercase">
              {5 - (streak % 5) === 5 ? t("travel.bonus") : t("travel.toBonus", { count: 5 - (streak % 5) })}
            </span>
          </div>
        </div>
      )}

      {onCooldown && ch.level >= 5 && (
        <button
          data-testid="fast-travel"
          onClick={() => step(true)}
          disabled={busy}
          className="w-full py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase flex items-center justify-center gap-2"
        >
          <Zap className="w-3 h-3" strokeWidth={1.5} /> {t("travel.fastTravel")}
        </button>
      )}

      <div className="panel" data-testid="adventure-log">
        <div className="px-3 py-2 border-b border-[#262626] flex justify-between items-center">
          <span className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">{t("travel.chronicle")}</span>
          <span className="font-mono text-[10px] text-[#666]">{log.length} entries</span>
        </div>
        <div className="max-h-[55vh] overflow-y-auto p-3 space-y-3">
          {log.length === 0 && (
            <p className="italic text-[#666] text-sm">The road is silent. Take your first step.</p>
          )}
          {log.map(e => (
            <div key={e.id} className="fade-up text-sm font-body leading-relaxed border-l-2 border-[#262626] pl-3">
              <span className={
                e.kind === "loot" ? "text-[#B89C3A]" :
                e.kind === "combat_start" || e.kind === "combat" ? "text-[#8A0303]" :
                e.kind === "reward" ? "text-[#E5C34A]" :
                e.kind === "gather" ? "text-[#6E8B3D]" :
                e.kind === "gather_offer" ? "text-[#B89C3A]" :
                e.kind === "gather_fail" ? "text-[#8A0303]" :
                e.kind === "quest_success" ? "text-[#4A7BC7]" :
                e.kind === "quest_fail" ? "text-[#8A0303]" :
                "text-[#B0ADA5]"
              }>
                {logTr[e.text] || e.text}
              </span>
              <div className="font-mono text-[9px] text-[#555] mt-1 uppercase tracking-widest">
                {e.kind}
              </div>
            </div>
          ))}
        </div>
      </div>

      {minigame && (
        <GatherMinigame
          mg={minigame}
          onClose={() => setMinigame(null)}
          onResolved={(r) => {
            if (r?.character) setCh(r.character);
          }}
        />
      )}
    </div>
  );
}
